// src/hooks/useGoogleCalendar.js
// Fetches Google Calendar events via /api/calendar-events, which uses the
// user's stored Gmail OAuth refresh token — no separate calendar auth needed.
// Calendar works automatically for anyone who has connected Gmail in Settings.

import { useState, useEffect, useCallback, useRef } from 'react'
import { getSessionWithTimeout } from '../lib/authSession'
import { supabase } from '../lib/supabase'
import { IS_DEMO } from '../lib/demoClient'
import { civilDayIn, isValidTimeZone, startOfCivilDayUTC } from '../lib/commitments'

const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/

const pad = (n) => String(n).padStart(2, '0')

function localRFC3339(date) {
  const offsetMin = date.getTimezoneOffset()
  const sign = offsetMin <= 0 ? '+' : '-'
  const absMin = Math.abs(offsetMin)
  const offH = pad(Math.floor(absMin / 60))
  const offM = pad(absMin % 60)
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${sign}${offH}:${offM}`
}

function localDateStr(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

async function fetchCalendarFromAPI(token, { timeMin, timeMax, timeZone = LOCAL_TZ, maxResults = 250 } = {}) {
  const params = new URLSearchParams({ timeZone, maxResults: String(maxResults) })
  if (timeMin) params.set('timeMin', timeMin)
  if (timeMax) params.set('timeMax', timeMax)

  const resp = await fetch(`/api/calendar-events?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  })

  if (resp.status === 401) return { error: 'not-connected', items: [] }
  if (!resp.ok) return { error: 'fetch-failed', items: [] }

  const data = await resp.json()
  return { error: data.error || null, items: data.items || [] }
}

// The trailer has no Google account: its calendar IS the seeded janet_meetings
// table (scripts/demo/seed.mjs → weekMeetings), read under RLS and shaped like
// Google events so every calendar surface renders it unchanged. The window is
// the whole current week plus the next — Home's week strip shows Mon–Fri, and
// the Google path's default (today onward) would leave the past days of the
// week empty, which is exactly what made the opening screen look idle.
//
// Retrieval is INTERVAL-OVERLAP, not "started inside the range": a meeting
// that began before rangeStart and is still running when rangeStart arrives
// (e.g. a multi-day production block) must still reach buildDailyAgenda,
// which already handles overlapping intervals correctly — the old
// `start_time >= rangeStart` filter dropped it before it ever got there.
// A row overlaps [rangeStart, rangeEnd) exactly when start_time < rangeEnd
// AND its effective end is after rangeStart. end_time is nullable and the
// mapper below falls back to start_time as the effective end for a
// zero-duration row, so that same fallback governs inclusion: a null-end_time
// row counts only when its start_time itself falls at-or-after rangeStart
// (there's no other instant to test), while a real end_time must be
// STRICTLY after rangeStart — a meeting that ended exactly at rangeStart has
// already finished and does not overlap.
async function fetchDemoCalendar({ rangeStart, rangeEnd }) {
  const startIso = rangeStart.toISOString()
  const endIso = rangeEnd.toISOString()
  const { data, error } = await supabase
    .from('janet_meetings')
    .select('id, title, start_time, end_time, attendees, hangout_link')
    .lt('start_time', endIso)
    .or(`end_time.gt.${startIso},and(end_time.is.null,start_time.gte.${startIso})`)
    .order('start_time', { ascending: true })
  if (error) return { error: 'fetch-failed', items: [] }
  return {
    error: null,
    items: (data || []).map((m) => ({
      id: m.id,
      summary: m.title,
      start: { dateTime: m.start_time },
      end: { dateTime: m.end_time || m.start_time },
      attendees: (m.attendees || []).map((a) => ({ email: a.email })),
      hangoutLink: m.hangout_link || undefined,
    })),
  }
}

function mondayOfThisWeek() {
  const d = new Date()
  const back = (d.getDay() + 6) % 7
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - back)
}

// `day`/`timeZone` are the AUTHORITATIVE viewer basis (Home's own
// meta.viewer.today/timeZone, server-derived in /api/dashboard-briefing —
// see HomeNorthStar.jsx's D.4 comment) — never required, since most callers
// (CalendarPage, Dashboard, EventNudge) have no such basis and fall back to
// the runtime's own clock/zone exactly as before. When supplied, the
// DEFAULT fetch window (no explicit startDate/endDate — i.e. every caller
// except CalendarPage's date-picker navigation) is anchored to the
// viewer's own civil day in their own zone, not the browser's: a viewer
// whose civil day has already turned over (or hasn't yet) relative to the
// runtime evaluating this hook must still get their own day's events, never
// a truthfully-empty-looking agenda built from the wrong day's window.
//
// `enabled` (2026-09-30 loading-order repair, Product Review finding on
// issue #178): DailyAgenda used to call this hook immediately with
// day=null/timeZone=null while meta.viewer (the authoritative basis) was
// still loading — this hook would fire a real request against the
// runtime-local fallback window, and if THAT settled before the
// authoritative request, DailyAgenda briefly presented a false agenda
// (events, or "nothing today") for the wrong civil day/timezone as if it
// were authoritative. Request ownership (below) already stops a stale
// resolution from later CLOBBERING the correct state, but it does nothing
// about this first, temporary mis-presentation. `enabled` (default true —
// every existing caller is unaffected) lets DailyAgenda defer starting the
// request at all until it actually has a real basis: while false, the
// mount effect never calls fetchEvents, and `loading` simply stays at its
// initial `true` — the honest "reading the calendar" pending state — for
// as long as the caller withholds a real day/timeZone.
export function useGoogleCalendar({ day = null, timeZone = null, enabled = true } = {}) {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [authed, setAuthed] = useState(false)
  const [error, setError] = useState(null)
  const sessionTokenRef = useRef(null)
  // Request ownership: the initial default-timezone request (fired before
  // an authoritative day/timeZone basis has arrived) and the later
  // authoritative request (fired once it has) are two SEPARATE async calls
  // to the same fetchEvents — nothing serializes them, so the earlier one
  // can resolve AFTER the later one. Every state-changing point below is
  // gated on "am I still the most recent request" so a stale resolution can
  // never overwrite the authoritative result, including on unmount.
  const requestIdRef = useRef(0)

  const configured = true // always available once Gmail is connected

  const fetchEvents = useCallback(async (startDate, endDate) => {
    const requestId = ++requestIdRef.current
    const isCurrent = () => requestIdRef.current === requestId
    setLoading(true)
    try {
      const { data: { session } } = await getSessionWithTimeout(8000)
      if (!isCurrent()) return
      const token = session?.access_token
      if (!token) {
        setError('not-connected')
        setAuthed(false)
        return
      }
      sessionTokenRef.current = token

      const now = new Date()
      const tz = isValidTimeZone(timeZone) ? timeZone : LOCAL_TZ
      const anchorDay = DAY_RE.test(day || '') ? day : civilDayIn(tz, now)
      const zonedStart = startOfCivilDayUTC(tz, anchorDay)
      const rangeStart = startDate || zonedStart || new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const rangeEnd = endDate || (() => {
        const d = new Date(rangeStart.getTime())
        d.setUTCDate(d.getUTCDate() + 5)
        return d
      })()
      // Whether THIS call carries a caller-supplied authoritative viewer
      // basis (DailyAgenda's day/timeZone, once meta has loaded) rather than
      // the hook's own runtime-local fallback. Distinct from an explicit
      // startDate (CalendarPage's date-picker), which already anchors
      // rangeStart/rangeEnd above regardless of this flag.
      const hasAuthoritativeBasis = DAY_RE.test(day || '') || isValidTimeZone(timeZone)

      const { error: apiErr, items } = IS_DEMO
        ? await fetchDemoCalendar(
            // An explicit range, or a supplied authoritative viewer day/
            // timeZone (DailyAgenda), both anchor to rangeStart/rangeEnd —
            // already computed above to cover exactly that day. Only a
            // caller with NEITHER (Dashboard, EventNudge, CalendarPage's own
            // first render before a range is picked) keeps the original
            // runtime-local Monday-of-week + 2-week window Home's week strip
            // reads.
            (startDate || hasAuthoritativeBasis)
              ? { rangeStart, rangeEnd }
              : (() => { const s = mondayOfThisWeek(); const e = new Date(s); e.setDate(e.getDate() + 14); return { rangeStart: s, rangeEnd: e } })()
          )
        : await fetchCalendarFromAPI(token, {
            // An EXPLICIT range (CalendarPage's own date-picker navigation)
            // keeps localRFC3339's browser-offset formatting, unchanged —
            // that Date came from a browser-local UI control, not from the
            // viewer's server-derived civil day. The computed default range
            // is already a precise UTC instant (startOfCivilDayUTC), so it
            // needs no offset formatting at all.
            timeMin: startDate ? localRFC3339(rangeStart) : rangeStart.toISOString(),
            timeMax: endDate ? localRFC3339(rangeEnd) : rangeEnd.toISOString(),
            timeZone: tz,
          })

      if (!isCurrent()) return

      if (apiErr) {
        setError(apiErr)
        setAuthed(false)
        return
      }

      setEvents(items)
      setAuthed(true)
      setError(null)
    } catch (err) {
      if (!isCurrent()) return
      console.error('[GCal]', err)
      setError('fetch-failed')
    } finally {
      if (isCurrent()) setLoading(false)
    }
    // day/timeZone are read in the closure above — a caller like DailyAgenda
    // that starts with no authoritative viewer basis yet (meta still
    // loading) and receives one moments later must re-fetch against the
    // real day/zone, not keep serving the window computed before it arrived.
  }, [day, timeZone])

  useEffect(() => {
    if (!enabled) {
      // Invalidate whatever the PRIOR effect run may have started (enabled
      // just flipped false, or day/timeZone changed while still disabled) —
      // nothing else will ever supersede it. Do not start a new request:
      // events/loading/error/authed are left exactly as they are, which for
      // a hook that has never fired is the honest initial pending state.
      requestIdRef.current += 1
      return
    }
    fetchEvents()
    // Invalidate any request this effect run started, whether deps changed
    // (a new fetchEvents identity is about to fire its own request anyway)
    // or the component unmounted (nothing else would ever supersede it).
    return () => { requestIdRef.current += 1 }
  }, [fetchEvents, enabled])

  // signIn: no popup needed — redirect to Settings to connect Gmail
  const signIn = useCallback(() => {
    window.location.href = '/settings'
  }, [])

  const signOut = useCallback(() => {
    sessionTokenRef.current = null
    setAuthed(false)
    setEvents([])
    setError(null)
  }, [])

  const refresh = useCallback(() => fetchEvents(), [fetchEvents])

  const fetchRange = useCallback((startDate, endDate) => fetchEvents(startDate, endDate), [fetchEvents])

  return { events, loading, authed, error, configured, signIn, signOut, refresh, fetchRange }
}

// ─── Exported helpers (unchanged) ───────────────────────────────────────────

export function getEventsForDate(events, date) {
  const dateStr = localDateStr(date)
  return events.filter(e => {
    if (e.start?.date) return e.start.date === dateStr
    const start = new Date(e.start?.dateTime)
    return localDateStr(start) === dateStr
  })
}

export function formatEventTime(event) {
  if (!event.start?.dateTime) return 'All day'
  const start = new Date(event.start.dateTime)
  return start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
}

export function formatEventTimeRange(event) {
  if (!event.start?.dateTime) return 'All day'
  const start = new Date(event.start.dateTime)
  const end = new Date(event.end.dateTime)
  const fmt = (d) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  return `${fmt(start)} – ${fmt(end)}`
}

const EVENT_COLORS = [
  { bg: 'bg-navy-50', border: 'border-navy-300', text: 'text-navy-600' },
  { bg: 'bg-terra-50', border: 'border-terra-200', text: 'text-terra-500' },
  { bg: 'bg-sage-50', border: 'border-sage-200', text: 'text-sage-500' },
  { bg: 'bg-warm-200', border: 'border-warm-400', text: 'text-warm-700' },
]

export function eventColor(event) {
  const str = event.summary || ''
  let hash = 0
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) | 0
  return EVENT_COLORS[Math.abs(hash) % EVENT_COLORS.length]
}
